import { FC, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import { Icon } from 'Components/UI';
import Button from 'Components/UI/Button';
import Select from 'Components/UI/Select';
import Form from 'Components/UI/Form/Form';
import Modal from 'Components/UI/Modal';
import Field from 'Components/UI/Field';
import { createSchema } from './UsuariosConvidar.schema';
import toastHandler from 'Utils/toastHandler';
import { REPEATED_EMAILS_ERROR } from 'Utils/Messages';
import { ITenantRole } from 'Data/interfaces/Tenant/ITenantRole';
import { ISharedUser } from 'Data/interfaces/User/ISharedUser';
import { IInvitedUser } from 'Data/interfaces/User/IInvitedUser';
import {
  ITenantSharedUsers,
  ITenantInvitedUsers
} from 'Data/interfaces/Tenant/ITenantUsersWithConstruction';
import { TenantActions } from 'Store/Tenant/Tenant.actions';
import { IUserInforResponse } from 'Data/interfaces/Auth/IUserInfoResponse';
import { ADMIN } from 'Utils/AcessPermission';
import { getTheme } from 'Store/MultiDomain/MultiDomain.selector';
import { Mixpanel } from 'Utils/MixPanel';
import { getCurrentTenant, getTenants } from 'Store/Tenant/Tenant.selector';
import styles from './UsuariosConvidados.module.scss';

interface IUsuariosConvidar {
  visible: boolean;
  tenantRoles: ITenantRole[];
  isLoading: boolean;
  tenantUsers: ITenantSharedUsers[];
  tenantInvitedUsers: ITenantInvitedUsers[];
  userIsFreemium?: boolean;
  limitUsers?: number;
  userInfo?: IUserInforResponse;
  planFree?: boolean;
  changeUsers: (changedUser: IUpdatedUsers) => void;
  onCancel?: () => void;
}
interface IUpdatedUsers {
  InvitedUsers: IInvitedUser[];
  SharedUsers: ISharedUser[];
}

const UsuariosConvidar: FC<IUsuariosConvidar> = ({
  tenantUsers,
  tenantInvitedUsers,
  tenantRoles,
  visible,
  isLoading,
  userIsFreemium,
  limitUsers,
  userInfo,
  planFree,
  changeUsers,
  onCancel,
}) => {
  const { t } = useTranslation('gerenciarUsuarios');
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const theme = useSelector(getTheme);
  const tenants = useSelector(getTenants);
  const currentTenant = useSelector(getCurrentTenant);

  const schema = useMemo(() => createSchema(t), [t]);

  const [NewInvitedUsers, setNewInvitedUsers] = useState<IInvitedUser[]>([]);
  const [newNameEmail, setNewNameEmail] = useState<string>('');
  const [RoleNewUser, setRoleNewUser] = useState<any>(4);
  const [isSendingData, setSendingData] = useState<boolean>(false);
  const [isOneUserToSend, setIsOneUserToSend] = useState<boolean>(true);
  const [disableInviteUsers, setDisableInviteUsers] = useState<boolean>(false);

  const hasRepeatedEmail = (email: string) => {
    return (
      tenantInvitedUsers.some(
        (user) => user?.InvitedUserEmail.toLowerCase() === email.toLowerCase()
      ) ||
      tenantUsers.some(
        (sharedUser) => sharedUser?.User?.Email?.toLowerCase() === email.toLowerCase()
      ) ||
      NewInvitedUsers.some((user) => user.Email.toLowerCase() === email.toLowerCase())
    );
  };
  const getSelectedSharedUsers = () => {
    return NewInvitedUsers.filter((sharedUser) => sharedUser);
  };

  const updateUsers = () => {
    changeUsers({
      InvitedUsers: getSelectedSharedUsers(),
      SharedUsers: getSelectedSharedUsers()
    });
  };

  const addInvitedUser = (data: any) => {
    if (hasRepeatedEmail(data.Email)) {
      toastHandler.showError(REPEATED_EMAILS_ERROR());
    } else {
      const newUser: IInvitedUser = {
        Email: data.Email.toLowerCase(),
        RoleFk: RoleNewUser,
        Privileges: [0, 1, 2],
        HasPlottingPermission: true
      };
      NewInvitedUsers.push(newUser);
      setNewInvitedUsers(NewInvitedUsers);
      updateUsers();
      setNewNameEmail('');
    }
  };

  const removeInvitedUser = (index: number) => {
    NewInvitedUsers.splice(index, 1);
    setNewInvitedUsers(NewInvitedUsers);
    updateUsers();
  };

  const roles = tenantRoles.map((role) => ({
    value: role.TenantRoleId,
    description: role.RoleName,
    label: role.RoleName
  }));

  const userOnFirstRow: IInvitedUser = {
    Email: newNameEmail,
    RoleFk: RoleNewUser,
    Privileges: [0, 1, 2],
    HasPlottingPermission: true
  };

  const sendInviteToUsers = () => {
    const filterTenantUsers = tenantUsers.filter(user => user.User.Id !== userInfo?.Id);
    const isFirstUser = tenantInvitedUsers.length <= 0 && filterTenantUsers.length <= 0 && (NewInvitedUsers.length > 0 || !!userOnFirstRow);

    if (NewInvitedUsers.length === 0 && newNameEmail !== '') {
      if (hasRepeatedEmail(newNameEmail)) {
        toastHandler.showError(REPEATED_EMAILS_ERROR());
      } else {
        NewInvitedUsers.push(userOnFirstRow);
        setNewInvitedUsers(NewInvitedUsers);
        updateUsers();
        dispatch(TenantActions.tenantBindInviedUsersRequest({
          Users: [userOnFirstRow],
          firstUserTracking: isFirstUser,
          userInfoTracking: userInfo,
          currentTenantTracking: currentTenant,
          tenantIds: tenants && tenants.length > 0
            ? tenants?.map((tenant) => tenant?.TenantId).filter((tenantId) => tenantId !== undefined) as number[]
            : undefined,
        }));
        setSendingData(true);
      }
    } else {
      if (hasRepeatedEmail(newNameEmail)) {
        toastHandler.showError(REPEATED_EMAILS_ERROR());
      } else if (NewInvitedUsers?.length > 0 && newNameEmail !== '') {
        NewInvitedUsers.push(userOnFirstRow);
        setNewNameEmail('');
        setNewInvitedUsers(NewInvitedUsers);
        dispatch(TenantActions.tenantBindInviedUsersRequest({
          Users: NewInvitedUsers,
          firstUserTracking: isFirstUser,
          userInfoTracking: userInfo,
          currentTenantTracking: currentTenant,
          tenantIds: tenants && tenants.length > 0
            ? tenants?.map((tenant) => tenant?.TenantId).filter((tenantId) => tenantId !== undefined) as number[]
            : undefined,
        }));
        setSendingData(true);
      } else if (NewInvitedUsers?.length > 0 && newNameEmail === '') {
        setNewInvitedUsers(NewInvitedUsers);
        dispatch(TenantActions.tenantBindInviedUsersRequest({
          Users: NewInvitedUsers,
          firstUserTracking: isFirstUser,
          userInfoTracking: userInfo,
          currentTenantTracking: currentTenant,
          tenantIds: tenants && tenants.length > 0
            ? tenants?.map((tenant) => tenant?.TenantId).filter((tenantId) => tenantId !== undefined) as number[]
            : undefined,
        }));
        setSendingData(true);
      }
    }
  };

  useEffect(() => {
    if (limitUsers) {
      const totalUsersOnConstructionNow = tenantInvitedUsers?.length + tenantUsers?.length;
      let MaxToAddUsersOnConstruction = limitUsers - totalUsersOnConstructionNow;
      if (NewInvitedUsers?.length >= MaxToAddUsersOnConstruction) {
        setDisableInviteUsers(true);
      } else {
        setDisableInviteUsers(false);
      }
    }
  }, [updateUsers]);

  if (isSendingData && !isLoading) {
    setSendingData(false);
    if (onCancel) {
      setNewNameEmail('');
      setNewInvitedUsers([]);
      onCancel();
    }
  }
  const closeModalResetForm = () => {
    if (onCancel) {
      setNewNameEmail('');
      setNewInvitedUsers([]);
      userOnFirstRow.Email = '';
      onCancel();
    }
    setNewNameEmail('');
    setNewInvitedUsers([]);
  };

  const redirectUsersPermissions = () => {
    if (planFree) {
      Mixpanel.track({
        name: 'CALL_T0_ACTION',
        props: {
          origin: 'convidar-usuarios-modal',
          originPath: window.location.pathname
        },
        userInfo,
        currentListTenant: currentTenant,
        tenantIds: tenants && tenants.length > 0
          ? tenants?.map((tenant) => tenant?.TenantId).filter((tenantId) => tenantId !== undefined) as number[]
          : undefined,
      });

      if (userInfo?.CurrentRoleFk === ADMIN) {
        return navigate('/faturamento/planos');
      }

      return window.open('https://bim.maletadoengenheiro.com.br/seja-premium');
    }

    return navigate('/gerenciar-usuarios/papeis');
  };

  const buttons = (
    <div>
      <Button
        loading={isLoading}
        className={styles['button']}
        type="primary"
        height={37}
        onClick={sendInviteToUsers}
      >
        {t('convidar.sendButton')}
      </Button>
    </div>
  );

  return (
    <Modal
      footerButtons={buttons}
      title={t('convidar.modalTitle')}
      onCancel={closeModalResetForm}
      visible={visible}
      width={798}
    >
      <div className={styles['usuariosConvidarWrapper']}>
        <Form schema={schema} onSubmit={addInvitedUser}>
          <div className={styles['newUsersWrapper']}>
            <Field
              disabled={disableInviteUsers ? true : false}
              name="Email"
              label={t('convidar.emailLabel')}
              defaultValue={newNameEmail}
              placeholder={t('convidar.emailPlaceholder')}
              className={styles['inputEmail']}
              onInput={setNewNameEmail}
            />
            <Select
              disabled={disableInviteUsers ? true : false}
              onSelect={setRoleNewUser}
              className={styles['selectRole']}
              placeholder={t('convidar.selectRole')}
              defaultValue={RoleNewUser}
              options={roles}
            />
            <Button
              disabled={disableInviteUsers ? true : false}
              htmlType="submit"
              className={styles['buttonNewUser']}
              onClick={() => setIsOneUserToSend(false)}
            >
              {disableInviteUsers ? (
                <Icon
                  color="cinzaOpaco"
                  className={styles['iconeNewUserDisaled']}
                  icon="adicionar"
                />
              ) : (
                <Icon
                  className={styles['iconeNewUser']}
                  color="primary"
                  icon="adicionar"
                />
              )}
            </Button>
          </div>
        </Form>
        <div>
          <div>
            {isOneUserToSend ? (
              <></>
            ) : (
              NewInvitedUsers.map((user, index) => (
                <Form schema={schema} onSubmit={() => { }} resetForm={false}>
                  <div className={styles['newUsersWrapper']}>
                    <Field
                      name="Email"
                      label={t('convidar.emailLabel')}
                      value={user.Email}
                      placeholder={t('convidar.emailPlaceholder')}
                      className={styles['inputEmail']}
                    />
                    <Select
                      onSelect={setRoleNewUser}
                      className={styles['selectRole']}
                      placeholder={t('convidar.selectRole')}
                      defaultValue={user.RoleFk}
                      options={roles}
                    />
                    <Button
                      htmlType="submit"
                      onClick={() => removeInvitedUser(index)}
                      className={styles['buttonNewUser']}
                    >
                      <Icon
                        color="cinzaSuperior"
                        icon="cancelar"
                      />
                    </Button>
                  </div>
                </Form>
              ))
            )}
          </div>
          <div className={styles['permissionsHintWrapper']}>
            <span>
              {t('convidar.rolesHint')}
              <span
                style={{ cursor: 'pointer', color: theme.colors.primary.primary }}
                onClick={redirectUsersPermissions}>
                {t('convidar.rolesHintLink')}
              </span>
              {t('convidar.rolesHintEnd')}
            </span>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default UsuariosConvidar;
